import {useContext, useState} from 'react';
import {useMutation} from 'react-query';

import {StepIndicator} from './StepIndicator';
import {BasicAddressContext, BasicAddressHeader, STEPS} from './BasicAddressContent';
import {Button, CustomInputBox, CustomSelect, FormValidator} from 'utils';

import API from 'api';
import {countryOptions} from '../data';
import {updateBasicAddress} from 'api/routes/address';
import {getError, getStates} from 'utils/helpers';
import AppContext from 'components/App/AppContext';

const defaultValues = {
    addressLandmark: '',
    alias: '',
    addressCountry: '',
    addressState: '',
    addressCity: '',
    addressLine1: '',
    addressLine2: '',
    flatNumber: '',
    buildingName: '',
    buildingNumber: '',
    postalCode: '',
};

const fetchValues = obj => {
    const result = {};
    const keys = Object.keys(defaultValues);
    for (const key of keys) {
        if (obj?.[key]) result[key] = obj?.[key];
    }
    return result;
};

export const BasicAddressStandard = () => {
    const {params} = useContext(AppContext);

    const {currentStep, result, handleStep} = useContext(BasicAddressContext);

    const [errorMsg, setErrorMsg] = useState();
    const [formValues, setFormValues] = useState({
        ...defaultValues,
        ...fetchValues(result),
    });
    const [formErrors, setFormErrors] = useState({});

    const {clientid, appname, apikey} = params || {};

    const handleSelect = (name, value) => {
        setFormValues(prev => ({...prev, [name]: value}));
        setFormErrors(prev => ({...prev, [name]: ''}));
    };

    const handleChange = ({target: {name, value}}) => handleSelect(name, value);

    const {mutate, isLoading} = useMutation(
        async payload => {
            setErrorMsg();
            const response = await API.post(updateBasicAddress, payload, {
                headers: {
                    clientid,
                    appname,
                    apikey,
                },
            });
            return response;
        },
        {
            onSuccess: ({data, status}) => {
                if (
                    (status === 200 || status === 201) &&
                    (data?.status_code === 200 || data?.status_code === 201)
                )
                    handleStep({
                        step: STEPS.BILL,
                        data: {
                            address_updated: true,
                            reference: data?.data?.reference,
                            ...formValues,
                        },
                    });
                // setSuccess(true);
                else setErrorMsg(data?.message);
            },
            onError: error => setErrorMsg(getError(error)),
        }
    );

    const handleSubmit = e => {
        e.preventDefault();

        const {isValid, formErrors} = FormValidator(formValues);

        if (isValid) {
            const payload = {
                ...formValues,
                reference: result?.id_result?.reference,
                addressCountry: formValues?.addressCountry?.label,
                addressState: formValues?.addressState?.label,
            };
            mutate(payload);
        } else setFormErrors(formErrors);
    };

    const renderStep = () => {
        return (
            <>
                <div className="w-100 mb-5">
                    {errorMsg && <h4 className="text-center text-danger py-4">{errorMsg}</h4>}
                    <CustomSelect
                        label="Country"
                        placeholder="Select your country"
                        options={countryOptions}
                        value={formValues?.addressCountry}
                        onChange={value => handleSelect('addressCountry', value)}
                        error={formErrors?.addressCountry}
                    />
                    <CustomSelect
                        label="State"
                        placeholder="Select your state"
                        options={getStates(formValues?.addressCountry?.code || 'NG')}
                        value={formValues?.addressState}
                        onChange={value => handleSelect('addressState', value)}
                        error={formErrors?.addressState}
                    />
                    <CustomInputBox
                        label="City"
                        name="addressCity"
                        placeholder="Enter City"
                        value={formValues?.addressCity}
                        onChange={handleChange}
                        error={formErrors?.addressCity}
                    />
                    <CustomInputBox
                        label="Flat Number"
                        name="flatNumber"
                        placeholder="Enter Flat Number"
                        value={formValues?.flatNumber}
                        onChange={handleChange}
                        error={formErrors?.flatNumber}
                    />
                    <CustomInputBox
                        label="Building Name"
                        name="buildingName"
                        placeholder="Enter Building Name"
                        value={formValues?.buildingName}
                        onChange={handleChange}
                        error={formErrors?.buildingName}
                    />
                    <CustomInputBox
                        label="Building Number"
                        name="buildingNumber"
                        placeholder="Enter Building Number"
                        value={formValues?.buildingNumber}
                        onChange={handleChange}
                        error={formErrors?.buildingNumber}
                    />
                    <CustomInputBox
                        label="Landmark"
                        name="addressLandmark"
                        placeholder="Enter Landmark"
                        value={formValues?.addressLandmark}
                        onChange={handleChange}
                        error={formErrors?.addressLandmark}
                        infoText="A popular building or place around your address"
                    />
                    <CustomInputBox
                        label="Address Line 1"
                        name="addressLine1"
                        placeholder="Enter Address Line 1"
                        value={formValues?.addressLine1}
                        onChange={handleChange}
                        error={formErrors?.addressLine1}
                    />
                    <CustomInputBox
                        label="Address Line 2"
                        name="addressLine2"
                        placeholder="Enter Address Line 2"
                        value={formValues?.addressLine2}
                        onChange={handleChange}
                        error={formErrors?.addressLine2}
                    />
                    <CustomInputBox
                        label="Alias"
                        name="alias"
                        placeholder="Enter Alias"
                        value={formValues?.alias}
                        onChange={handleChange}
                        error={formErrors?.alias}
                    />
                    <CustomInputBox
                        cls="mb-5"
                        label="Postal Code"
                        name="postalCode"
                        placeholder="Enter Postal Code"
                        value={formValues?.postalCode}
                        onChange={handleChange}
                        error={formErrors?.postalCode}
                    />
                </div>
                <Button
                    cls="w-100 btn btn-lg rounded-0"
                    name={isLoading ? 'Loading' : 'Continue'}
                    handleClick={handleSubmit}
                    disabled={isLoading}
                />
            </>
        );
    };

    return (
        <div className="d-flex flex-column align-items-center">
            {/* <div className="w-100">
                <BackArrowIcon
                    className="pointer back-arrow"
                    onClick={() => handleStep({step: STEPS.LIVENESS_RESULT})}
                />
            </div> */}
            <StepIndicator currentStep={currentStep} />
            <BasicAddressHeader
                title="Enter Address"
                text={<>Enter your address in detail (N/A for fields that are not applicable)</>}
                cls="text-center mb-4"
            />
            {renderStep()}
        </div>
    );
};
